import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Collapse,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import DnerhsApi from "api/DnerhsApi";
import Alert from "components/Alert";
import history from "utils/History";
import MatrizHorariosModal from "components/MatrizHorariosModal";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AssignmentIcon from "@material-ui/icons/Assignment";
import SchoolIcon from "@material-ui/icons/School";
import PersonIcon from "@material-ui/icons/Person";
import DateRangeIcon from "@material-ui/icons/DateRange";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PeopleIcon from "@material-ui/icons/People";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import DescriptionIcon from "@material-ui/icons/Description";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { trackPromise } from "react-promise-tracker";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    color: "white",
    boxShadow: theme.shadows[4],
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    color: "white",
  },
  titleIcon: {
    fontSize: 40,
  },
  backButton: {
    color: "white",
    borderColor: "white",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderColor: "white",
    },
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[2],
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "& .MuiCardHeader-title": {
      fontWeight: 600,
      fontSize: "1.1rem",
    },
  },
  cardHeaderAvatar: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  infoGrid: {
    padding: theme.spacing(2),
  },
  infoItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: "#f8f9fa",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#e9ecef",
    },
  },
  infoIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
    marginTop: 2,
  },
  infoContent: {
    flex: 1,
  },
  label: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  value: {
    color: theme.palette.text.primary,
    fontSize: "1rem",
    fontWeight: 500,
  },
  estadoChip: {
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: theme.spacing(2, 1),
  },
  infoRow: {
    display: "flex",
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& th": {
      color: "white",
      fontWeight: 700,
      fontSize: "0.9rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
      transition: "background-color 0.2s",
    },
  },
  observacionCard: {
    backgroundColor: theme.palette.warning.light,
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    marginTop: theme.spacing(2),
  },
  observacionIcon: {
    color: theme.palette.warning.main,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  statsCard: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    textAlign: "center",
    boxShadow: theme.shadows[3],
  },
  statsValue: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  statsLabel: {
    fontSize: "0.85rem",
    opacity: 0.9,
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
}));

const DetallePractica = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { cronogramaId } = useParams();
  const [practica, setPractica] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [observaciones, setObservaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showObservaciones, setShowObservaciones] = useState(false);
  const [matrizModalOpen, setMatrizModalOpen] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);

  useEffect(() => {
    loadData();
  }, [cronogramaId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Obtener datos de la práctica con resumen (incluye cantidades y horas calculadas)
      const practicaResponse = await trackPromise(
        DnerhsApi.get(`/convenios/practicas/${cronogramaId}/resumen`)
      );
      setPractica(practicaResponse.data);

      // Obtener detalles completos de estudiantes (nuevo endpoint optimizado)
      const detallesResponse = await trackPromise(
        DnerhsApi.get(`/practicas-detalles/${cronogramaId}/detalles-completos`)
      );
      setDetalles(detallesResponse.data);

      // Obtener historial de observaciones
      const observacionesResponse = await trackPromise(
        DnerhsApi.get(`/practicas-observaciones/${cronogramaId}/observaciones`)
      );
      setObservaciones(observacionesResponse.data);

    } catch (error) {
      console.error("Error al cargar datos:", error);
      Alert.showServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    history.goBack();
  };

  const handleOpenMatrizHorarios = (tutor) => {
    setSelectedTutor(tutor);
    setMatrizModalOpen(true);
  };

  const handleCloseMatrizHorarios = () => {
    setMatrizModalOpen(false);
    setSelectedTutor(null);
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!practica) {
    return (
      <Box className={classes.root}>
        <Typography variant="h6" color="error">
          No se encontró la práctica
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleVolver}
          style={{ marginTop: 16 }}
        >
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* HEADER PROFESIONAL */}
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <Box className={classes.title}>
            <AssignmentIcon className={classes.titleIcon} />
            <Box>
              <Typography variant="h4" style={{ fontWeight: 700, marginBottom: 4 }}>
                Detalle de Cronograma
              </Typography>
              <Typography variant="subtitle1" style={{ opacity: 0.9 }}>
                ID: {cronogramaId} | {practica.materia || "N/A"}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={handleVolver}
            size="large"
          >
            Volver
          </Button>
        </Box>
      </Box>

      {/* INFORMACIÓN GENERAL - DISEÑO PROFESIONAL */}
      <Card className={classes.card}>
        <CardHeader
          avatar={
            <Avatar className={classes.cardHeaderAvatar}>
              <InfoOutlinedIcon />
            </Avatar>
          }
          title="Información General"
          className={classes.cardHeader}
        />
        <CardContent className={classes.infoGrid}>
          <Grid container spacing={2}>
            {/* FILA 1: IDs + Fechas + Estado (TODO EN LÍNEA HORIZONTAL) */}
            <Grid item xs={12}>
              <Paper
                elevation={1}
                style={{
                  padding: "12px 16px",
                  backgroundColor: "#fafafa",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 16,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AssignmentIcon style={{ fontSize: 20, color: "#1976d2" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#666" }}>ID:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 700, color: "#333" }}>{practica.id}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <AssignmentIcon style={{ fontSize: 20, color: "#1976d2" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#666" }}>Convenio:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 700, color: "#333" }}>{practica.convenioId || "N/A"}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <DateRangeIcon style={{ fontSize: 20, color: "#1976d2" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#666" }}>Desde:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 700, color: "#333" }}>{practica.fechaDesde || "N/A"}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <DateRangeIcon style={{ fontSize: 20, color: "#1976d2" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#666" }}>Hasta:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 700, color: "#333" }}>{practica.fechaHasta || "N/A"}</Typography>
                </Box>

                <Chip
                  label={practica.estado || "Sin estado"}
                  color="primary"
                  size="small"
                  style={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Paper>
            </Grid>

            {/* FILA 2: Institución/Sede + Estadísticas (TODO INLINE) */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {/* Institución/Sede - Badge horizontal compacto */}
                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={2}
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#e8f5e9",
                      borderRadius: 8,
                      border: "2px solid #4caf50",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      height: "100%",
                    }}
                  >
                    <SchoolIcon style={{ color: "#2e7d32", fontSize: 32 }} />
                    <Box flex={1}>
                      <Typography
                        variant="body1"
                        style={{
                          fontWeight: 700,
                          color: "#2e7d32",
                          fontSize: "0.95rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {practica.institucion || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: "#388e3c",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        Sede: {practica.sede || "N/A"}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Estadísticas - 3 cards inline */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Paper
                        elevation={2}
                        style={{
                          padding: "10px 8px",
                          textAlign: "center",
                          backgroundColor: "#e3f2fd",
                          borderRadius: 8,
                          border: "2px solid #2196f3",
                        }}
                      >
                        <PeopleIcon style={{ fontSize: 24, color: "#1976d2", marginBottom: 2 }} />
                        <Typography style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1565c0" }}>
                          {practica.cantidadEstudiantes || 0}
                        </Typography>
                        <Typography variant="caption" style={{ color: "#1976d2", fontWeight: 600, fontSize: "0.65rem" }}>
                          Estudiantes
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={4}>
                      <Paper
                        elevation={2}
                        style={{
                          padding: "10px 8px",
                          textAlign: "center",
                          backgroundColor: "#e8f5e9",
                          borderRadius: 8,
                          border: "2px solid #4caf50",
                        }}
                      >
                        <AccessTimeIcon style={{ fontSize: 24, color: "#388e3c", marginBottom: 2 }} />
                        <Typography style={{ fontSize: "1.2rem", fontWeight: 700, color: "#2e7d32" }}>
                          {practica.horasSemanales || 0}
                        </Typography>
                        <Typography variant="caption" style={{ color: "#388e3c", fontWeight: 600, fontSize: "0.65rem" }}>
                          Hrs. Semanales
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={4}>
                      <Paper
                        elevation={2}
                        style={{
                          padding: "10px 8px",
                          textAlign: "center",
                          backgroundColor: "#fff3e0",
                          borderRadius: 8,
                          border: "2px solid #ff9800",
                        }}
                      >
                        <ScheduleIcon style={{ fontSize: 24, color: "#f57c00", marginBottom: 2 }} />
                        <Typography style={{ fontSize: "1.2rem", fontWeight: 700, color: "#e65100" }}>
                          {practica.horasTotales || 0}
                        </Typography>
                        <Typography variant="caption" style={{ color: "#f57c00", fontWeight: 600, fontSize: "0.65rem" }}>
                          Hrs. Totales
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* FILA 3: Carrera + Materia (con tooltip para textos largos) */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Tooltip title={practica.carreraDescripcion || "N/A"} arrow>
                    <Box className={classes.infoItem}>
                      <SchoolIcon className={classes.infoIcon} />
                      <Box className={classes.infoContent}>
                        <Typography className={classes.label}>Carrera</Typography>
                        <Typography
                          className={classes.value}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {practica.carreraDescripcion || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Tooltip title={practica.materia || "N/A"} arrow>
                    <Box className={classes.infoItem}>
                      <DescriptionIcon className={classes.infoIcon} />
                      <Box className={classes.infoContent}>
                        <Typography className={classes.label}>Materia</Typography>
                        <Typography
                          className={classes.value}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {practica.materia || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>

            {/* FILA 4: Curso/Semestre/Año + Observaciones (inline) */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {/* Curso, Semestre, Año */}
                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={1}
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#fafafa",
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                      gap: 16,
                    }}
                  >
                    <Box textAlign="center">
                      <Typography variant="caption" style={{ color: "#666", fontWeight: 600 }}>Curso</Typography>
                      <Typography variant="h6" style={{ fontWeight: 700, color: "#333" }}>{practica.curso || "N/A"}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="caption" style={{ color: "#666", fontWeight: 600 }}>Semestre</Typography>
                      <Typography variant="h6" style={{ fontWeight: 700, color: "#333" }}>{practica.semestre || "N/A"}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <CalendarTodayIcon style={{ fontSize: 16, color: "#1976d2", marginBottom: 2 }} />
                      <Typography variant="caption" style={{ color: "#666", fontWeight: 600, display: "block" }}>Año</Typography>
                      <Typography variant="h6" style={{ fontWeight: 700, color: "#333" }}>{practica.anio || "N/A"}</Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Observaciones - Expandible inline */}
                <Grid item xs={12} md={7}>
                  <Paper
                    elevation={2}
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setShowObservaciones(!showObservaciones)}
                      style={{
                        padding: "10px 16px",
                        backgroundColor: "#e3f2fd",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: showObservaciones ? "2px solid #2196f3" : "none",
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <DescriptionIcon style={{ marginRight: 8, color: "#1976d2", fontSize: 20 }} />
                        <Typography variant="body2" style={{ fontWeight: 700, color: "#1565c0" }}>
                          Historial de Observaciones ({observaciones && observaciones.length ? observaciones.length : 0})
                        </Typography>
                      </Box>
                      <ExpandMoreIcon
                        style={{
                          color: "#1976d2",
                          fontSize: 20,
                          transform: showObservaciones ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s",
                        }}
                      />
                    </Box>
                    <Collapse in={showObservaciones} timeout="auto" unmountOnExit>
                      <Box style={{ padding: 12, maxHeight: 200, overflowY: "auto" }}>
                        {observaciones && observaciones.length > 0 ? (
                          observaciones.map((obs, index) => (
                            <Paper
                              key={index}
                              elevation={1}
                              style={{
                                padding: 10,
                                marginBottom: index < observaciones.length - 1 ? 8 : 0,
                                backgroundColor: "#fff",
                                borderLeft: "3px solid #2196f3",
                                borderRadius: 6,
                              }}
                            >
                              <Box display="flex" alignItems="flex-start">
                                <InfoOutlinedIcon
                                  style={{
                                    marginRight: 6,
                                    marginTop: 2,
                                    color: "#1976d2",
                                    fontSize: 16,
                                  }}
                                />
                                <Box flex={1}>
                                  <Typography
                                    variant="caption"
                                    style={{
                                      fontWeight: 700,
                                      color: "#666",
                                      fontSize: "0.65rem",
                                    }}
                                  >
                                    {obs.fechaCreacion || obs.fechaObservacion || "Fecha no disponible"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      marginTop: 2,
                                      color: "#333",
                                      fontSize: "0.85rem",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {obs.observacion}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          ))
                        ) : (
                          <Typography variant="body2" style={{ color: "#666", fontStyle: "italic", textAlign: "center", padding: 12, fontSize: "0.85rem" }}>
                            No hay observaciones registradas para este cronograma.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* ESTUDIANTES ASIGNADOS - DISEÑO COMPACTO Y PROFESIONAL */}
      {detalles && detalles.length > 0 && (
        <Card className={classes.card}>
          <CardHeader
            avatar={
              <Avatar className={classes.cardHeaderAvatar}>
                <PeopleIcon />
              </Avatar>
            }
            title={`Estudiantes Asignados (${detalles.length})`}
            className={classes.cardHeader}
          />
          <CardContent style={{ padding: 0 }}>
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell style={{ padding: "10px 16px", fontWeight: 700, fontSize: "0.8rem" }}>Estudiante</TableCell>
                    <TableCell style={{ padding: "10px 16px", fontWeight: 700, fontSize: "0.8rem" }}>Establecimiento</TableCell>
                    <TableCell style={{ padding: "10px 16px", fontWeight: 700, fontSize: "0.8rem" }}>Tutor</TableCell>
                    <TableCell style={{ padding: "10px 16px", fontWeight: 700, fontSize: "0.8rem", textAlign: "center" }}>Horarios</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalles.map((detalle, index) => (
                    <TableRow
                      key={index}
                      className={classes.tableRow}
                      style={{
                        transition: "background-color 0.2s",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f5f5"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <TableCell style={{ padding: "8px 16px" }}>
                        <Box display="flex" alignItems="center">
                          <Avatar
                            style={{
                              marginRight: 10,
                              width: 28,
                              height: 28,
                              backgroundColor: "#1976d2",
                              fontSize: "0.9rem",
                            }}
                          >
                            <PersonIcon style={{ fontSize: 16 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.85rem", lineHeight: 1.3 }}>
                              {`${detalle.estudianteNombres || ""} ${detalle.estudianteApellidos || ""}`.trim() || "N/A"}
                            </Typography>
                            {detalle.estudianteCedula && (
                              <Typography variant="caption" style={{ color: "#666", fontSize: "0.7rem" }}>
                                CI: {detalle.estudianteCedula}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell style={{ padding: "8px 16px" }}>
                        <Box display="flex" alignItems="center">
                          <LocalHospitalIcon style={{ marginRight: 6, fontSize: 18, color: "#1976d2" }} />
                          <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                            {detalle.establecimientoNombre || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell style={{ padding: "8px 16px" }}>
                        {detalle.tutorNombres && detalle.tutorApellidos ? (
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.85rem", lineHeight: 1.3 }}>
                              {`${detalle.tutorNombres} ${detalle.tutorApellidos}`}
                            </Typography>
                            {detalle.tutorCedula && (
                              <Typography variant="caption" style={{ color: "#666", fontSize: "0.7rem" }}>
                                CI: {detalle.tutorCedula}
                              </Typography>
                            )}
                            <Box marginTop={0.5}>
                              <Chip
                                icon={<CalendarTodayIcon style={{ fontSize: 14 }} />}
                                label="Ver Horarios"
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={() => handleOpenMatrizHorarios({
                                  id: detalle.tutorId,
                                  nombre: `${detalle.tutorNombres} ${detalle.tutorApellidos}`,
                                  cedula: detalle.tutorCedula,
                                })}
                                style={{
                                  fontSize: "0.7rem",
                                  height: 24,
                                  cursor: "pointer",
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" style={{ color: "#999", fontSize: "0.85rem" }}>N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell style={{ padding: "8px 16px" }}>
                        {detalle.horarios ? (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {(() => {
                              // Parsear el string de horarios
                              const horariosArray = detalle.horarios.split(' | ');
                              const diasSemana = {
                                'Lunes': { abrev: 'L', color: '#e3f2fd', textColor: '#1565c0' },
                                'Martes': { abrev: 'M', color: '#fff3e0', textColor: '#e65100' },
                                'Miércoles': { abrev: 'X', color: '#e8f5e9', textColor: '#2e7d32' },
                                'Jueves': { abrev: 'J', color: '#fce4ec', textColor: '#c2185b' },
                                'Viernes': { abrev: 'V', color: '#fff9c4', textColor: '#f57f17' },
                                'Sábado': { abrev: 'S', color: '#e0f2f1', textColor: '#00695c' },
                                'Domingo': { abrev: 'D', color: '#fbe9e7', textColor: '#bf360c' },
                              };

                              return horariosArray.map((horario, idx) => {
                                // Regex corregido para capturar días con tildes (Miércoles, Sábado)
                                const match = horario.match(/^([a-záéíóúñ]+):\s*(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})$/i);
                                if (!match) return null;

                                const [, dia, desde, hasta] = match;
                                const diaInfo = diasSemana[dia] || { abrev: dia[0], color: '#f5f5f5', textColor: '#666' };

                                return (
                                  <Tooltip key={idx} title={`${dia}: ${desde} - ${hasta}`} arrow>
                                    <Box
                                      style={{
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        backgroundColor: diaInfo.color,
                                        borderRadius: 6,
                                        padding: '4px 6px',
                                        minWidth: 40,
                                        border: `1px solid ${diaInfo.textColor}40`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        style={{
                                          fontWeight: 700,
                                          fontSize: '0.75rem',
                                          color: diaInfo.textColor,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {diaInfo.abrev}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        style={{
                                          fontSize: '0.65rem',
                                          color: diaInfo.textColor,
                                          lineHeight: 1.2,
                                          marginTop: 2,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {desde.substring(0, 5)}-{hasta.substring(0, 5)}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                );
                              });
                            })()}
                          </Box>
                        ) : (
                          <Typography variant="caption" style={{ color: "#999", fontSize: "0.75rem" }}>
                            Sin horarios
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Modal de Matriz de Horarios */}
      {selectedTutor && (
        <MatrizHorariosModal
          open={matrizModalOpen}
          onClose={handleCloseMatrizHorarios}
          tutorId={selectedTutor.id}
          tutorNombre={selectedTutor.nombre}
          tutorCedula={selectedTutor.cedula}
        />
      )}
    </Box>
  );
};

export default DetallePractica;
